import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useState } from 'react';
import { Alert, Card, Collapse } from 'react-bootstrap';
import { validateCard } from '../validators';
import BlockRenderer from './BlockRenderer';
import './CardRenderer.css';
import { getCustomCard } from './cards';
import ErrorBoundary from './ErrorBoundary';

interface CardData {
  card_id?: string;
  title?: string;
  collapse_settings?: {
    open?: boolean;
    title?: string;
  };
  blocks?: any[];
  block_layout?: string;
}

interface CardRendererProps {
  card: CardData;
}

const CardRenderer: React.FC<CardRendererProps> = ({ card }) => {
  const [isOpen, setIsOpen] = useState<boolean>(
    card?.collapse_settings?.open ?? true
  );

  // Validate card data
  const validation = validateCard(card);
  if (!validation.isValid) {
    console.warn('Card validation failed:', validation.errors);
    return (
      <Card className="mb-4 error-boundary-card">
        <Card.Body>
          <Alert variant="warning">
            <Alert.Heading>⚠️ Invalid Card Data</Alert.Heading>
            <p className="mb-2 text-muted small">
              <strong>ept AI backend error.</strong> Please send an email to{' '}
              <a href="mailto:support@ept.ai">support@ept.ai</a> with a
              screenshot or the text below.
            </p>
            <p className="mb-1">
              This card has invalid data and cannot be displayed:
            </p>
            <ul className="mb-0">
              {validation.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  // Check for custom cards based on card_id
  const CustomCardComponent = getCustomCard(card.card_id);
  if (CustomCardComponent) {
    return <CustomCardComponent card={card} />;
  }

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const renderHeader = () => {
    if (!card.title) return null;

    if (card.collapse_settings) {
      return (
        <Card.Header
          as="h5"
          onClick={handleToggle}
          className="collapsible-header"
        >
          <span>{card.title || card.collapse_settings.title}</span>
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </Card.Header>
      );
    }

    return <Card.Header as="h5">{card.title}</Card.Header>;
  };

  return (
    <Card className="mb-4">
      {renderHeader()}
      <Collapse in={!card.collapse_settings || isOpen}>
        <div>
          <Card.Body>
            <div className={`blocks-container ${card.block_layout || 'row'}`}>
              {card.blocks &&
                card.blocks.map((block, blockIndex) => {
                  // console.log('Rendering block:', block);
                  return (
                    <ErrorBoundary
                      key={blockIndex}
                      fallbackType="block"
                      title="Block Error"
                    >
                      <BlockRenderer
                        block={block}
                        cardContext={{
                          card_id: card.card_id,
                          title: card.title,
                        }}
                      />
                    </ErrorBoundary>
                  );
                })}
            </div>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

export default CardRenderer;
